import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function InnerSanctumBanner() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/vault/waitlist", { email });
      toast.success(data.message || "Your name is on the list.");
      setJoined(true);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add you to the list.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="inner-sanctum"
      data-testid="inner-sanctum-banner"
      className="relative w-full min-h-[96svh] overflow-hidden bg-black"
    >
      {/* forge vault key art */}
      <div
        className="absolute inset-0 bg-center bg-cover"
        style={{ backgroundImage: "url(/art/inner_sanctum.jpg)" }}
        role="img"
        aria-label="A colossal molten vault door in the heart of the forge"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(255,90,0,0.12),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/95 pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 min-h-[96svh] flex flex-col justify-center text-center py-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.45em] text-[#ff5a00] mb-6"
        >
          The Studio Itself
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          data-testid="sanctum-headline"
          className="font-display uppercase text-[#f5f0e8] leading-[0.9] tracking-tight drop-shadow-[0_3px_36px_rgba(0,0,0,0.9)]"
          style={{ fontSize: "clamp(3rem, 10vw, 9rem)" }}
        >
          The
          <br />
          <span
            className="italic"
            style={{
              color: "#ff5a00",
              textShadow: "0 2px 40px rgba(255,143,0,0.35)",
            }}
          >
            Inner Sanctum
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="font-display italic text-lg sm:text-2xl text-[#ededed] mt-10 max-w-2xl mx-auto leading-relaxed"
        >
          Every world above is only the surface. Behind this door is the studio itself —
          where the rare things are made and sold on a timeline and a price no one else can meet.
          Registration opens soon. The list is short by design.
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.4 }}
          onSubmit={submit}
          data-testid="sanctum-form"
          className="mt-12 flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-lg mx-auto w-full"
        >
          <input
            data-testid="sanctum-email"
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={joined}
            className="flex-1 bg-black/60 backdrop-blur border border-[#ff5a00]/30 text-[#f5f0e8] placeholder:text-[#7a7a7a] px-5 py-4 font-body focus:outline-none focus:border-[#ff5a00] transition-colors disabled:opacity-60"
          />
          <button
            data-testid="sanctum-submit"
            type="submit"
            disabled={loading || joined}
            className="font-mono text-xs uppercase tracking-[0.28em] bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] transition-colors px-8 py-4 rounded-none disabled:opacity-60"
          >
            {joined ? "You're on the list" : loading ? "Sending…" : "Add my name"}
          </button>
        </motion.form>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a] mt-6"
        >
          By invitation · One signal when the door opens · No noise
        </motion.p>
      </div>
    </section>
  );
}
